import React from 'react'

// @src imports
import { useActions, useQueries, useVertical } from 'src/hooks'
import { Icon, NavLink, Transition } from 'src/chrome'
import suspenseBoundary from 'src/chrome/SuspenseBoundary/suspenseBoundaryHOC'
import { AccountRoute } from 'src/dashboard/routes/AccountRoute'
import { NotificationsRoute } from 'src/dashboard/routes/NotificationsRoute'
import { DraftedOrdersRoute } from 'src/dashboard/routes/DraftedOrdersRoute'
import { DraftedCardsRoute } from 'src/dashboard/routes/DraftedCardsRoute'
import { CardHistoryRoute } from 'src/dashboard/routes/CardHistoryRoute'
import { PaymentSettingsRoute } from 'src/dashboard/routes/PaymentSettingsRoute'
import { PricingRoute } from 'src/marketing/plans/routes/PricingRoute'

import styles from './dashboardNav.module.scss'
import { AwardsRoute } from 'src/dashboard/routes/AwardsRoute'
import { getNotificationCount } from '../../../graphql'
import { Logo } from '@sendoutcards/quantum-design-ui'

const DashboardNav: React.FC<{}> = () => {
  const isAwardsPageVisible = false
  const vertical = useVertical()
  const isClientSoc = vertical.id === 'soc'

  const [notificationCount] = useQueries(getNotificationCount())

  const actions = useActions()

  const IconColor = '#6F8394'

  return (
    <div className={styles.dashboardNav}>
      <div className={styles.logo}>
        <NavLink onClick={actions.openCatalog}>
          {vertical.id === 'soc' ? (
            <Icon
              icon={'SOCLOGO'}
              viewBox={'0 0 77 72'}
              size={50}
              color={'#e687ff'}
            />
          ) : (
            <Logo
              brand={vertical.brand ? vertical.brand : 'soc'}
              color="color"
              type="icon"
              width={
                vertical.brand !== 'streamMarketing' &&
                vertical.brand !== 'blue42' &&
                vertical.brand !== 'bellaCadeaux'
                  ? '50px'
                  : '200px'
              }
            />
          )}
        </NavLink>
      </div>
      <div>
        <li>
          <span className={styles.linkIcon}>
            <Icon icon={'USER2'} size={18} color={IconColor} />
          </span>
          <NavLink
            activeClassName={styles.active}
            route={AccountRoute}
            onClick={actions.openAccount}
          >
            My Account
          </NavLink>
        </li>
        {isClientSoc && (
          <li>
            {notificationCount > 0 && (
              <NavLink onClick={actions.openNotifications}>
                <span
                  className={`${styles.notificationBadge} ${styles.linkIcon}`}
                  style={{ bottom: 0, right: 2 }}
                >
                  {notificationCount <= 10 ? notificationCount : '10+'}
                </span>
              </NavLink>
            )}
            <NavLink
              id="goto_notifications_btn"
              activeClassName={styles.active}
              route={NotificationsRoute}
              onClick={actions.openNotifications}
            >
              Notifications
            </NavLink>
          </li>
        )}
        {isAwardsPageVisible && (
          <li>
            <span className={styles.linkIcon}>
              <Icon icon={'CHECKCIRCLE'} size={16} color={IconColor} />
            </span>
            <NavLink
              id={'goto_awards_btn'}
              activeClassName={styles.active}
              route={AwardsRoute}
              onClick={actions.openAwards}
            >
              Awards
            </NavLink>
          </li>
        )}
        <li>
          <span className={styles.linkIcon}>
            <Icon icon={'CREDITCARD'} size={16} color={IconColor} />
          </span>
          <NavLink
            id={'goto_payment_settings_btn'}
            activeClassName={styles.active}
            route={PaymentSettingsRoute}
            onClick={actions.openPaymentSettings}
          >
            Payment Settings
          </NavLink>
        </li>
        {isClientSoc && (
          <>
            <li>
              <span className={styles.linkIcon}>
                <Icon icon={'DOLLARSIGN'} size={16} color={IconColor} />
              </span>
              <NavLink
                id={'goto_pricing_btn'}
                activeClassName={styles.active}
                route={PricingRoute}
                onClick={actions.openPricing}
              >
                Pricing
              </NavLink>
            </li>
            <li>
              <span className={styles.linkIcon}>
                <Icon icon={'CATALOG'} size={16} color={IconColor} />
              </span>
              <NavLink
                id="goto_drafted_cards_btn"
                activeClassName={styles.active}
                route={DraftedCardsRoute}
                onClick={actions.openDraftedCards}
              >
                Drafted Cards
              </NavLink>
            </li>
            <li>
              <span className={styles.linkIcon}>
                <Icon icon={'LIST'} size={15} color={IconColor} />
              </span>
              <NavLink
                id="goto_drafted_orders_btn"
                activeClassName={styles.active}
                route={DraftedOrdersRoute}
                onClick={actions.openDraftedOrders}
              >
                Drafted Orders
              </NavLink>
            </li>
            <li>
              <span className={styles.linkIcon} style={{ marginRight: 3 }}>
                <Icon
                  icon={'ENVELOPE'}
                  height={15}
                  width={25}
                  viewBox={'200 0 1024 1024'}
                  color={IconColor}
                />
              </span>
              <NavLink
                id={'goto_card_history_btn'}
                activeClassName={styles.active}
                route={CardHistoryRoute}
                onClick={actions.openCardHistory}
              >
                Card History
              </NavLink>
            </li>
            <li className={styles.backToEditor}>
              <span className={styles.linkIcon}>
                <Icon icon={'CHEVRONLEFT'} size={15} color={IconColor} />
              </span>
              <NavLink id="goto_catalog_btn" onClick={actions.openCatalog}>
                Card Catalog
              </NavLink>
            </li>
          </>
        )}
      </div>
    </div>
  )
}

export default suspenseBoundary({
  component: DashboardNav,
  unresolved: <Transition message={'Loading...'} />,
  failure: error => `Failed loading: ${error}`,
})
